import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

/**
 * Hook to get the count of pending reviews for courses the current user can grade
 */
export function usePendingReviewCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["pending-review-count", user?.id],
    queryFn: async () => {
      if (!user) return 0;

      // First check if user is admin
      const { data: adminRole } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      let courseIds: string[] = [];

      if (adminRole) {
        // Admins can see all courses
        const { data: allCourses } = await supabase
          .from("courses")
          .select("id");
        courseIds = allCourses?.map(c => c.id) || [];
      } else {
        // Get courses this user is assigned to with grading permission
        const { data: assignments } = await supabase
          .from("course_instructors")
          .select("course_id")
          .eq("instructor_id", user.id)
          .eq("can_grade", true);
        courseIds = assignments?.map(a => a.course_id) || [];
      }

      if (courseIds.length === 0) return 0;

      // Get submissions for assignments in modules of these courses
      const { data: modules } = await supabase
        .from("modules")
        .select("id")
        .in("course_id", courseIds);

      if (!modules?.length) return 0;

      const moduleIds = modules.map(m => m.id);

      const { data: assignments } = await supabase
        .from("assignments")
        .select("id")
        .in("module_id", moduleIds);

      if (!assignments?.length) return 0;

      const assignmentIds = assignments.map(a => a.id);

      const { data, error } = await supabase
        .from("assignment_submissions")
        .select("id")
        .in("assignment_id", assignmentIds)
        .eq("status", "submitted");

      if (error) throw error;
      return data?.length || 0;
    },
    enabled: !!user,
    refetchInterval: 60000,
  });
}

export interface PendingSubmission {
  id: string;
  submitted_at: string;
  status: string;
  assignment: {
    id: string;
    title: string;
    module: {
      id: string;
      title: string;
      course: {
        id: string;
        title: string;
        thumbnail_url: string | null;
      };
    };
  };
  profiles: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  } | null;
}

/**
 * Hook to get detailed pending submissions for courses the current user can grade
 */
export function usePendingSubmissions() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["pending-submissions", user?.id],
    queryFn: async (): Promise<PendingSubmission[]> => {
      if (!user) return [];

      // First check if user is admin
      const { data: adminRole } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      let courseIds: string[] = [];

      if (adminRole) {
        // Admins can see all courses
        const { data: allCourses } = await supabase
          .from("courses")
          .select("id");
        courseIds = allCourses?.map(c => c.id) || [];
      } else {
        // Get courses this user is assigned to with grading permission
        const { data: assignments } = await supabase
          .from("course_instructors")
          .select("course_id")
          .eq("instructor_id", user.id)
          .eq("can_grade", true);
        courseIds = assignments?.map(a => a.course_id) || [];
      }

      if (courseIds.length === 0) return [];

      // Get modules for these courses
      const { data: modules } = await supabase
        .from("modules")
        .select("id, title, course_id, courses(id, title, thumbnail_url)")
        .in("course_id", courseIds);

      if (!modules?.length) return [];

      const moduleIds = modules.map(m => m.id);
      const moduleMap = new Map(modules.map(m => [m.id, m]));

      // Get assignments for these modules
      const { data: assignmentsData } = await supabase
        .from("assignments")
        .select("id, title, module_id")
        .in("module_id", moduleIds);

      if (!assignmentsData?.length) return [];

      const assignmentIds = assignmentsData.map(a => a.id);
      const assignmentMap = new Map(assignmentsData.map(a => [a.id, a]));

      // Get pending submissions
      const { data: submissions, error } = await supabase
        .from("assignment_submissions")
        .select("id, submitted_at, status, user_id, assignment_id")
        .in("assignment_id", assignmentIds)
        .eq("status", "submitted")
        .order("submitted_at", { ascending: true });

      if (error) throw error;
      if (!submissions?.length) return [];

      // Get profiles for all submitters
      const userIds = [...new Set(submissions.map(s => s.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Transform data
      return submissions.map(sub => {
        const assignment = assignmentMap.get(sub.assignment_id);
        const module = assignment ? moduleMap.get(assignment.module_id) : null;
        const course = module?.courses as { id: string; title: string; thumbnail_url: string | null } | null;
        const profile = profileMap.get(sub.user_id);

        return {
          id: sub.id,
          submitted_at: sub.submitted_at,
          status: sub.status,
          assignment: {
            id: assignment?.id || "",
            title: assignment?.title || "Unknown Assignment",
            module: {
              id: module?.id || "",
              title: module?.title || "Unknown Module",
              course: {
                id: course?.id || "",
                title: course?.title || "Unknown Course",
                thumbnail_url: course?.thumbnail_url || null,
              },
            },
          },
          profiles: profile || null,
        };
      });
    },
    enabled: !!user,
    refetchInterval: 60000,
  });
}

/**
 * Hook to check if current user can grade a specific course
 */
export function useCanGradeCourse(courseId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["can-grade-course", user?.id, courseId],
    queryFn: async () => {
      if (!user || !courseId) return false;

      // Check if admin
      const { data: adminRole } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (adminRole) return true;

      // Check course_instructors
      const { data: assignment } = await supabase
        .from("course_instructors")
        .select("id")
        .eq("course_id", courseId)
        .eq("instructor_id", user.id)
        .eq("can_grade", true)
        .maybeSingle();

      return !!assignment;
    },
    enabled: !!user && !!courseId,
  });
}
